/**
 * ShaftBerry Animation - "Neon Energy Overload" (ENHANCED VERSION)
 * 
 * FRAMER MOTION VERSION: Ultimate neon energy experience!
 * - Berry with charging face expression (closed eyes concentrating)
 * - Color-shifting holographic aura (tier 3+)
 * - Forked lightning with multiple branches (tier 3+)
 * - Energy crescendo buildup with wave burst (tier 4+)
 * - Chromatic aberration glitch effect (tier 4+)
 * - Cyber text variations (ZAP!, BZZT!, SURGE!, etc.)
 * - Electron orbit particles (tier 2+)
 * - Portal vortex with spiral (tier 6)
 * 
 * Synchronized with sound effects.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useAnimationSound from '../../../hooks/useAnimationSound';
import { getPhasesForCandy } from './soundRegistry';

const ShaftBerryAnimation = ({ intensity = 1, onComplete }) => {
    const [phase, setPhase] = useState('charging');

    // Sound integration
    const { playPhaseSound, preloadSounds } = useAnimationSound('ShaftBerry', intensity, true);

    // Device-aware particle scaling for performance
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const particleMultiplier = isMobile ? 0.4 : 1;

    // Scaled effect counts with mobile optimization
    const arcCount = Math.floor((4 + intensity * 2) * particleMultiplier);
    const plasmaParticles = Math.floor((8 + intensity * 6) * particleMultiplier);
    const ringCount = Math.floor((3 + intensity) * particleMultiplier);
    const lightningBolts = intensity >= 3 ? Math.floor((intensity - 1) * particleMultiplier) : 0;
    const electronCount = Math.floor((3 + intensity) * particleMultiplier);

    // Tier-based features
    const pulseSpeed = 1.1 - (intensity * 0.1);
    const showGlitch = intensity >= 3;
    const showEnergyRings = intensity >= 2;
    const showLightning = intensity >= 3;
    const showMatrixRain = intensity >= 5;
    const showPortal = intensity >= 6;
    const showHologram = intensity >= 4;
    const saturationBoost = intensity >= 5;
    const showChargingFace = true;
    const showColorShift = intensity >= 3;
    const showWaveBurst = intensity >= 4;
    const showChromatic = intensity >= 4;
    const showElectrons = intensity >= 2;

    // Animation timing
    const baseTiming = intensity >= 5 ? 1.15 : 1;

    // Cyber text pool
    const cyberTexts = useMemo(() => ['ZAP!', 'BZZT!', 'SURGE!', 'SHOCK!', 'VOLT!'], []);
    const cyberText = useMemo(() => cyberTexts[Math.floor(Math.random() * cyberTexts.length)], [cyberTexts]);

    // Neon colors
    const neonColors = useMemo(() =>
        ['#A855F7', '#7C3AED', '#EC4899', '#8B5CF6', '#C084FC', '#F472B6', '#06B6D4', '#22D3EE'], []);

    // Stable ref for playPhaseSound to avoid re-triggering animation useEffect
    const playPhaseSoundRef = React.useRef(playPhaseSound);
    playPhaseSoundRef.current = playPhaseSound;

    // Stable ref for onComplete
    const onCompleteRef = React.useRef(onComplete);
    onCompleteRef.current = onComplete;

    // Preload sounds (only once on mount)
    useEffect(() => {
        preloadSounds(getPhasesForCandy('ShaftBerry'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Extended animation sequence
    useEffect(() => {
        const timers = [];

        playPhaseSoundRef.current('charging');

        timers.push(setTimeout(() => {
            setPhase('pulsing');
            playPhaseSoundRef.current('pulsing');
        }, 330 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('revealing');
            playPhaseSoundRef.current('revealing');
        }, 600 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('energizing');
            playPhaseSoundRef.current('energizing');
        }, 920 * baseTiming));

        timers.push(setTimeout(() => {
            setPhase('complete');
            onCompleteRef.current?.();
        }, 2300 * baseTiming));

        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Generate particles
    const arcs = useMemo(() =>
        Array.from({ length: arcCount }, (_, i) => ({
            id: i,
            angle: (360 / arcCount) * i + Math.random() * 12,
            length: 45 + Math.random() * 55,
            delay: i * 0.05,
            thickness: 2 + Math.random() * 2,
        })), [arcCount]);

    const plasma = useMemo(() =>
        Array.from({ length: plasmaParticles }, (_, i) => ({
            id: i,
            angle: Math.random() * 360,
            distance: 45 + Math.random() * 80,
            delay: Math.random() * 0.3,
            size: 5 + Math.random() * 10,
            color: neonColors[i % neonColors.length],
        })), [plasmaParticles, neonColors]);

    const rings = useMemo(() =>
        Array.from({ length: ringCount }, (_, i) => ({
            id: i,
            size: 110 + i * 30,
            delay: i * 0.08,
            speed: 1.8 + i * 0.4,
            tilt: 65 + i * 8,
            color: neonColors[i % neonColors.length],
        })), [ringCount, neonColors]);

    const electrons = useMemo(() =>
        Array.from({ length: electronCount }, (_, i) => ({
            id: i,
            orbitSize: 70 + i * 20,
            speed: 1.5 + i * 0.3,
            startAngle: (360 / electronCount) * i,
            color: neonColors[i % neonColors.length],
        })), [electronCount, neonColors]);

    const matrixDrops = useMemo(() =>
        showMatrixRain ? Array.from({ length: (intensity - 4) * 10 }, (_, i) => ({
            id: i,
            x: -110 + Math.random() * 220,
            delay: Math.random() * 0.5,
            speed: 0.5 + Math.random() * 0.35,
        })) : [], [showMatrixRain, intensity]);

    const waveBurstRings = useMemo(() =>
        Array.from({ length: 3 }, (_, i) => ({
            id: i,
            delay: i * 0.15,
            size: 80 + i * 40,
        })), []);

    return (
        <div
            className={`relative flex items-center justify-center overflow-visible
                w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64
                ${saturationBoost ? 'saturate-150' : ''}
            `}
        >
            {/* === PORTAL BACKGROUND (enhanced with spiral) === */}
            <AnimatePresence>
                {showPortal && phase === 'energizing' && (
                    <>
                        <motion.div
                            className="absolute w-52 h-52 rounded-full"
                            style={{
                                background: 'conic-gradient(from 0deg, #A855F7, #EC4899, #06B6D4, #7C3AED, #A855F7)',
                                filter: 'blur(25px)',
                                opacity: 0.45,
                            }}
                            animate={{ rotate: 360 }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                        />
                        <motion.div
                            className="absolute w-40 h-40 rounded-full"
                            style={{
                                background: 'conic-gradient(from 180deg, #7C3AED, #EC4899, #A855F7, #7C3AED)',
                                filter: 'blur(15px)',
                                opacity: 0.3,
                            }}
                            animate={{ rotate: -360 }}
                            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                        />
                    </>
                )}
            </AnimatePresence>

            {/* === COLOR-SHIFTING AURA (tier 3+) === */}
            <AnimatePresence>
                {showColorShift && (phase === 'revealing' || phase === 'energizing') && (
                    <motion.div
                        className="absolute w-36 h-36 rounded-full"
                        style={{
                            background: 'radial-gradient(circle, transparent 30%, rgba(168,85,247,0.4) 100%)',
                        }}
                        initial={{ opacity: 0 }}
                        animate={{
                            opacity: [0, 0.8, 0.6],
                            background: [
                                'radial-gradient(circle, transparent 30%, rgba(168,85,247,0.4) 100%)',
                                'radial-gradient(circle, transparent 30%, rgba(236,72,153,0.4) 100%)',
                                'radial-gradient(circle, transparent 30%, rgba(6,182,212,0.4) 100%)',
                                'radial-gradient(circle, transparent 30%, rgba(168,85,247,0.4) 100%)',
                            ],
                        }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                    />
                )}
            </AnimatePresence>

            {/* === ELECTRON ORBITS (tier 2+) === */}
            <AnimatePresence>
                {showElectrons && (phase === 'revealing' || phase === 'energizing') && electrons.map((e) => (
                    <motion.div
                        key={`electron-${e.id}`}
                        className="absolute"
                        style={{
                            width: e.orbitSize,
                            height: e.orbitSize,
                        }}
                        initial={{ rotate: e.startAngle }}
                        animate={{ rotate: e.startAngle + 360 }}
                        transition={{ duration: e.speed, repeat: Infinity, ease: 'linear' }}
                    >
                        <motion.div
                            className="absolute w-3 h-3 rounded-full"
                            style={{
                                background: e.color,
                                boxShadow: `0 0 10px ${e.color}, 0 0 20px ${e.color}`,
                                top: 0,
                                left: '50%',
                                marginLeft: -6,
                            }}
                        />
                    </motion.div>
                ))}
            </AnimatePresence>

            {/* === ENERGY RINGS (enhanced with colors) === */}
            <AnimatePresence>
                {showEnergyRings && (phase === 'revealing' || phase === 'energizing') && (
                    <>
                        {rings.map((ring) => (
                            <motion.div
                                key={ring.id}
                                className="absolute rounded-full"
                                style={{
                                    width: ring.size,
                                    height: ring.size * 0.28,
                                    border: `2px solid ${ring.color}`,
                                    opacity: 0.7,
                                    transform: `rotateX(${ring.tilt}deg)`,
                                    boxShadow: `0 0 10px ${ring.color}`,
                                }}
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{
                                    opacity: [0, 0.9, 0.7],
                                    scale: 1,
                                    rotateZ: 360,
                                }}
                                transition={{
                                    opacity: { delay: ring.delay, duration: 0.25 },
                                    scale: { delay: ring.delay, duration: 0.25 },
                                    rotateZ: { duration: ring.speed, repeat: Infinity, ease: 'linear' },
                                }}
                            />
                        ))}
                    </>
                )}
            </AnimatePresence>

            {/* === NEON BERRY (enhanced with face) === */}
            <motion.div
                className="relative"
                animate={
                    phase === 'charging' ? { scale: [0, 1.15, 1], opacity: [0, 1] } :
                        phase === 'pulsing' ? {
                            scale: [1, 1.08, 1],
                            transition: { duration: pulseSpeed, repeat: Infinity }
                        } :
                            phase === 'revealing' ? { scale: 1.12 } :
                                phase === 'energizing' ? { scale: [1.12, 1.18, 1.12] } : {}
                }
                transition={{ duration: 0.35 }}
            >
                {/* Chromatic Aberration (tier 4+) */}
                {showChromatic && (phase === 'revealing' || phase === 'energizing') && (
                    <>
                        <motion.div
                            className="absolute w-26 h-26 rounded-full"
                            style={{
                                width: '6.5rem',
                                height: '6.5rem',
                                background: 'rgba(236,72,153,0.3)',
                                left: -4,
                                filter: 'blur(2px)',
                            }}
                            animate={{ x: [-3, 3, -3] }}
                            transition={{ duration: 0.1, repeat: Infinity }}
                        />
                        <motion.div
                            className="absolute w-26 h-26 rounded-full"
                            style={{
                                width: '6.5rem',
                                height: '6.5rem',
                                background: 'rgba(6,182,212,0.3)',
                                left: 4,
                                filter: 'blur(2px)',
                            }}
                            animate={{ x: [3, -3, 3] }}
                            transition={{ duration: 0.1, repeat: Infinity }}
                        />
                    </>
                )}

                <motion.div
                    className="w-26 h-26 rounded-full relative overflow-hidden"
                    style={{
                        width: '6.5rem',
                        height: '6.5rem',
                        background: 'radial-gradient(circle at 30% 30%, #C084FC 0%, #A855F7 40%, #7C3AED 100%)',
                        boxShadow: `0 0 35px #A855F7, 0 0 70px #7C3AED, inset 0 -10px 25px rgba(0,0,0,0.35)`,
                    }}
                >
                    {/* Inner glow */}
                    <motion.div
                        className="absolute inset-4 rounded-full"
                        style={{
                            background: 'radial-gradient(circle, rgba(255,255,255,0.5) 0%, transparent 70%)',
                        }}
                        animate={{
                            scale: [1, 1.25, 1],
                            opacity: [0.5, 0.9, 0.5],
                        }}
                        transition={{ duration: pulseSpeed, repeat: Infinity }}
                    />

                    {/* === FACE EXPRESSION === */}
                    {showChargingFace && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            {/* Eyes */}
                            <div className="flex gap-4 mb-1">
                                {[0, 1].map((i) => (
                                    <motion.div
                                        key={i}
                                        className="relative"
                                    >
                                        {phase === 'charging' || phase === 'pulsing' ? (
                                            /* Closed eyes - concentrating */
                                            <motion.div
                                                className="w-5 h-1 bg-white/80 rounded"
                                                style={{ transform: `rotate(${i === 0 ? 5 : -5}deg)` }}
                                                animate={{ scaleY: [1, 0.5, 1] }}
                                                transition={{ duration: 0.3, repeat: Infinity }}
                                            />
                                        ) : (
                                            /* Open eyes - energized */
                                            <motion.div
                                                className="w-5 h-5 bg-white rounded-full flex items-center justify-center overflow-hidden"
                                                style={{ boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2), 0 0 10px rgba(255,255,255,0.5)' }}
                                                animate={phase === 'energizing' ? {
                                                    scale: [1, 1.1, 1],
                                                } : {}}
                                                transition={{ duration: 0.2, repeat: Infinity }}
                                            >
                                                <motion.div
                                                    className="w-3 h-3 rounded-full"
                                                    style={{
                                                        background: 'linear-gradient(135deg, #7C3AED, #EC4899)',
                                                        boxShadow: '0 0 5px #A855F7',
                                                    }}
                                                    animate={{ scale: [1, 1.2, 1] }}
                                                    transition={{ duration: 0.15, repeat: Infinity }}
                                                />
                                            </motion.div>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                            {/* Mouth */}
                            <motion.div className="mt-1">
                                {phase === 'charging' || phase === 'pulsing' ? (
                                    <motion.div
                                        className="w-3 h-3 bg-white/70 rounded-full"
                                        animate={{ scale: [1, 0.8, 1] }}
                                        transition={{ duration: 0.4, repeat: Infinity }}
                                    />
                                ) : phase === 'revealing' ? (
                                    <motion.div
                                        className="w-5 h-2 bg-white/80 rounded-t-full"
                                    />
                                ) : (
                                    <motion.div
                                        className="w-6 h-4 bg-white/90 rounded-full flex items-center justify-center"
                                        animate={{ scale: [1, 1.1, 1] }}
                                        transition={{ duration: 0.15, repeat: Infinity }}
                                    >
                                        <span className="text-[8px] text-purple-600 font-bold">!</span>
                                    </motion.div>
                                )}
                            </motion.div>
                        </div>
                    )}

                    {/* Pulse ring */}
                    {phase === 'pulsing' && (
                        <>
                            <motion.div
                                className="absolute inset-0 rounded-full"
                                style={{ border: '3px solid rgba(168, 85, 247, 0.7)' }}
                                animate={{ scale: [1, 1.6], opacity: [0.9, 0] }}
                                transition={{ duration: pulseSpeed, repeat: Infinity }}
                            />
                            <motion.div
                                className="absolute inset-0 rounded-full"
                                style={{ border: '2px solid rgba(236, 72, 153, 0.5)' }}
                                animate={{ scale: [1, 1.8], opacity: [0.7, 0] }}
                                transition={{ duration: pulseSpeed, delay: pulseSpeed / 2, repeat: Infinity }}
                            />
                        </>
                    )}

                    {/* Hologram scanlines (enhanced) */}
                    {showHologram && (
                        <div className="absolute inset-0 overflow-hidden rounded-full">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute w-full h-[2px]"
                                    style={{
                                        top: `${i * 10}%`,
                                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                    }}
                                    animate={{ x: [-120, 120] }}
                                    transition={{
                                        duration: 0.4 + Math.random() * 0.4,
                                        repeat: Infinity,
                                        repeatType: 'reverse',
                                        delay: i * 0.08,
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    {/* Glitch slices (enhanced) */}
                    {showGlitch && phase === 'energizing' && (
                        <>
                            {[0, 1, 2, 3, 4].map((i) => (
                                <motion.div
                                    key={i}
                                    className="absolute w-full h-3"
                                    style={{
                                        top: `${15 + i * 14}%`,
                                        background: i % 2 === 0 ? 'rgba(6,182,212,0.4)' : 'rgba(236,72,153,0.4)',
                                    }}
                                    animate={{
                                        x: [0, 12, -8, 0],
                                        opacity: [0, 1, 1, 0],
                                    }}
                                    transition={{
                                        duration: 0.12,
                                        repeat: Infinity,
                                        repeatDelay: 0.4 + Math.random() * 0.4,
                                        delay: i * 0.04,
                                    }}
                                />
                            ))}
                        </>
                    )}
                </motion.div>
            </motion.div>

            {/* === WAVE BURST RINGS (tier 4+) === */}
            <AnimatePresence>
                {showWaveBurst && phase === 'energizing' && waveBurstRings.map((ring) => (
                    <motion.div
                        key={`wave-${ring.id}`}
                        className="absolute rounded-full"
                        style={{
                            width: ring.size,
                            height: ring.size,
                            border: '3px solid',
                            borderImage: 'linear-gradient(135deg, #A855F7, #EC4899, #06B6D4) 1',
                            borderRadius: '50%',
                        }}
                        initial={{ scale: 0, opacity: 1 }}
                        animate={{ scale: 2.5, opacity: 0 }}
                        transition={{ delay: ring.delay, duration: 0.8, repeat: 2, repeatDelay: 0.5 }}
                    />
                ))}
            </AnimatePresence>

            {/* === ELECTRIC ARCS (enhanced) === */}
            <AnimatePresence>
                {(phase === 'revealing' || phase === 'energizing') && arcs.map((arc) => (
                    <motion.div
                        key={arc.id}
                        className="absolute"
                        style={{
                            width: arc.thickness,
                            height: arc.length,
                            background: 'linear-gradient(to bottom, #EC4899, #A855F7, transparent)',
                            transformOrigin: 'top center',
                            left: '50%',
                            top: '50%',
                            marginLeft: -arc.thickness / 2,
                            rotate: arc.angle,
                            boxShadow: '0 0 12px #A855F7, 0 0 25px #EC4899',
                            borderRadius: '2px',
                        }}
                        initial={{ scaleY: 0, opacity: 0 }}
                        animate={{ scaleY: [0, 1, 0.85, 1], opacity: [0, 1, 0.85, 1] }}
                        exit={{ opacity: 0 }}
                        transition={{ delay: arc.delay, duration: 0.25 }}
                    />
                ))}
            </AnimatePresence>

            {/* === PLASMA PARTICLES (enhanced) === */}
            <AnimatePresence>
                {phase === 'energizing' && plasma.map((p) => (
                    <motion.div
                        key={p.id}
                        className="absolute rounded-full"
                        style={{
                            width: p.size,
                            height: p.size,
                            background: p.color,
                            boxShadow: `0 0 12px ${p.color}, 0 0 25px ${p.color}`,
                        }}
                        initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
                        animate={{
                            x: Math.cos((p.angle * Math.PI) / 180) * p.distance,
                            y: Math.sin((p.angle * Math.PI) / 180) * p.distance,
                            scale: [0, 1.2, 0],
                            opacity: [0, 1, 0],
                        }}
                        transition={{ delay: p.delay, duration: 0.7 }}
                    />
                ))}
            </AnimatePresence>

            {/* === FORKED LIGHTNING (enhanced) === */}
            <AnimatePresence>
                {showLightning && phase === 'energizing' && Array.from({ length: lightningBolts }).map((_, i) => {
                    const angle = (360 / lightningBolts) * i + Math.random() * 25;
                    return (
                        <motion.div
                            key={i}
                            className="absolute"
                            style={{
                                left: '50%',
                                top: '50%',
                                transform: `rotate(${angle}deg)`,
                            }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0, 1, 0.6, 1, 0] }}
                            transition={{ delay: i * 0.1, duration: 0.35, repeat: 3 }}
                        >
                            {/* Main bolt */}
                            <div
                                className="w-1.5 h-24"
                                style={{
                                    background: 'linear-gradient(to bottom, #FFFFFF, #A855F7, #EC4899)',
                                    boxShadow: '0 0 15px #A855F7, 0 0 30px #FFFFFF',
                                    clipPath: 'polygon(50% 0%, 65% 25%, 100% 30%, 60% 45%, 75% 100%, 45% 55%, 0% 50%, 40% 35%)',
                                }}
                            />
                            {/* Fork branch */}
                            <div
                                className="absolute w-1 h-12"
                                style={{
                                    background: 'linear-gradient(to bottom, #FFFFFF, #A855F7)',
                                    boxShadow: '0 0 8px #A855F7',
                                    top: '40%',
                                    left: '60%',
                                    transform: 'rotate(30deg)',
                                    clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                                }}
                            />
                        </motion.div>
                    );
                })}
            </AnimatePresence>

            {/* === MATRIX RAIN (enhanced) === */}
            <AnimatePresence>
                {showMatrixRain && phase === 'energizing' && matrixDrops.map((drop) => (
                    <motion.div
                        key={drop.id}
                        className="absolute text-xs font-mono"
                        style={{
                            left: `calc(50% + ${drop.x}px)`,
                            top: -25,
                            color: '#22D3EE',
                            textShadow: '0 0 8px #22D3EE, 0 0 15px #06B6D4',
                        }}
                        initial={{ y: 0, opacity: 0 }}
                        animate={{ y: 160, opacity: [0, 1, 0] }}
                        transition={{ delay: drop.delay, duration: drop.speed }}
                    >
                        {String.fromCharCode(0x30A0 + Math.random() * 96)}
                    </motion.div>
                ))}
            </AnimatePresence>

            {/* === OUTER GLOW (enhanced) === */}
            {phase === 'energizing' && (
                <>
                    <motion.div
                        className="absolute w-44 h-44 rounded-full"
                        style={{
                            background: 'radial-gradient(circle, rgba(168,85,247,0.3) 0%, transparent 70%)',
                        }}
                        animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0.9, 0.5] }}
                        transition={{ duration: 0.7, repeat: Infinity }}
                    />
                    <motion.div
                        className="absolute w-36 h-36 rounded-full"
                        style={{
                            background: 'radial-gradient(circle, rgba(236,72,153,0.2) 0%, transparent 70%)',
                        }}
                        animate={{ scale: [1.1, 1, 1.1], opacity: [0.4, 0.7, 0.4] }}
                        transition={{ duration: 0.5, repeat: Infinity }}
                    />
                </>
            )}

            {/* === CYBER TEXT (enhanced pool) === */}
            <AnimatePresence>
                {intensity >= 4 && phase === 'energizing' && (
                    <motion.div
                        className="absolute text-4xl font-bold z-20"
                        style={{
                            color: '#A855F7',
                            textShadow: '3px 3px 0 #7C3AED, -3px -3px 0 #7C3AED, 0 0 30px #A855F7, 0 0 60px #EC4899',
                            fontFamily: 'Impact, sans-serif',
                        }}
                        initial={{ scale: 0, rotate: -20 }}
                        animate={{ scale: [0, 1.4, 1.1], rotate: [0, 8, 0] }}
                        transition={{ delay: 0.15, duration: 0.35 }}
                    >
                        {cyberText}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ShaftBerryAnimation;
